import { useQuery } from '@apollo/client';
import { type FunctionComponent } from 'react';
import { configureRoute } from '../../../utils/routes/configure-route';
import { type HomePageQuery, type HomePageQueryVariables } from '../../__generated__/graphql-client-types';
import { PageLoading } from '../../components/common-components/loading/loading.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { FiveHundred } from '../../components/error-components/500/500.component';
import { AnimatedSplash } from '../../components/home-components/animated-splash/animated-splash.component';
import { HomeCarousel } from '../../components/home-components/home-carousel/home-carousel.component';
import { isAnimatedSplashSection } from '../../components/home-components/home-common.helpers';
import { GenerateInViewHomeSection } from '../../components/home-components/home-section-factory/home-section-factory.component';
import { HomeSplash } from '../../components/home-components/home-splash/home-splash.component';
import { DEFAULT_META_DESCRIPTION, FEATURE_FLAGS, SITE_NAME } from '../../constants/general';
import { HOMEPAGE_LINK } from '../../constants/links';
import { useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { useDyRecommendationContext, DyRecommendationContextType } from '../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useFeature } from '../../hooks/features/features.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { HOMEPAGE } from '../../queries/homepage/homepage.queries';
import { visuallyHidden } from './home.css';

export const Home: FunctionComponent = () => {
	const { search } = useRouterLocation();
	const params = new URLSearchParams(search);
	const contentItemId = params.get('contentitemid');
	const { data, error, loading } = useQuery<HomePageQuery, HomePageQueryVariables>(HOMEPAGE, {
		fetchPolicy: 'cache-first',
		variables: { input: { ...(contentItemId ? { contentItemId } : {}) } }
	});
	const homepageContent = data?.homepage;
	// TODO: EFDC-32612 Remove this flag once both splashes are moved to the customization section
	const showAnimatedSplash = useFeature(FEATURE_FLAGS.HOMEPAGE_PROJECTS_FIRST);

	useTrackPageView({ pageName: 'home' }, { page: 'home' }, loading);
	useDyRecommendationContext({ type: DyRecommendationContextType.HOME_PAGE });

	if (loading) {
		return <PageLoading />;
	} else if (error) {
		return <FiveHundred />;
	}
	return (
		<div className="bg-theme-white">
			{homepageContent && (
				<PageContainer
					canonicalURL={HOMEPAGE_LINK}
					pageTitle="Bathroom, Kitchen - Faucets, Sinks, Hardware - Build with Ferguson"
					metaDescription={DEFAULT_META_DESCRIPTION}
					layout="minimized"
					showUpdateLocation={true}>
					<>
						{showAnimatedSplash ? (
							homepageContent?.sections.map(
								(section, index) =>
									isAnimatedSplashSection(section) && (
										<li className="flex flex-column w-100 pa0 mh0 mv3" key={index}>
											<AnimatedSplash {...section} image={section.animatedSplashImage} />
										</li>
									)
							)
						) : (
							<HomeSplash {...homepageContent.splash} />
						)}
						<HomeCarousel
							items={homepageContent.saleCategories.items}
							automationHook="carousel-top-category-swipe"
							navigationVerticalPosition={-40}
						/>
						<ul className="list ma0 pa0 flex flex-column justify-between items-center w-100" data-testid="sections">
							{homepageContent?.sections.map(
								(section, index) =>
									!isAnimatedSplashSection(section) && (
										<li className="flex flex-column w-100 pa0 mh0 mt6 mb0" key={index}>
											<GenerateInViewHomeSection {...section} />
										</li>
									)
							)}
						</ul>
					</>
				</PageContainer>
			)}
			<h1 className={`overflow-hidden absolute bn pa0 ${visuallyHidden}`}>
				Find Bathroom and Kitchen Sinks, Faucets and Hardware on {SITE_NAME}
			</h1>
		</div>
	);
};

export const RoutedHomePage = configureRoute({ path: HOMEPAGE_LINK, ddRum: { rumViewName: 'Home' } }, Home);
